/**
 * @module app.track.edit
 */

import appBase from '../index.js';
import appInteractionDrawTrack from '../interaction/DrawTrack.js';
import appInteractionModifyTrack from '../interaction/ModifyTrack.js';
import {calculateViaPoints, smallScreen} from '../utils.js';
import {measurePointsAlongLineString, snapCoordinatesToLineString} from '../graphhopperutil.js';
import {MAIN_MENU_EVENT_NAME, TRACK_EVENT_NAME} from "../constants";
import {listen, listenOnce, unlistenByKey} from "ol/events";
import {snapToNetwork} from "../hiddenfeature";
import {matchBestEffort} from "./match";


class Controller {

  /**
   * @param {angular.Scope} $scope Angular scope.
   * @param {angularGettext.Catalog} gettextCatalog
   * @param {ui.router.$stateParams} $stateParams
   * @param {string} graphhopperUrl graphhopper url.
   * @param {ngeo.statemanager.Location} ngeoLocation Location service from NGEO.
   * @param {app.query.state.Service} appQueryStateService
   * @param {app.MapService} appMapService
   * @param {app.MobileInteractionService} appMobileInteractionService
   * @param {app.waiting} appWaiting
   * @param {app.AuthenticationService} appAuthenticationService
   * @ngInject
   */
  constructor($scope, gettextCatalog, $stateParams, graphhopperUrl, ngeoLocation, appQueryStateService,
    appMapService, appMobileInteractionService, appWaiting, appAuthenticationService) {

    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    this.gettextCatalog = gettextCatalog;

    /**
     * @private
     */
    this.mainCtrl_ = $scope.$root['mainCtrl'];

    /**
     * @private
     */
    this.stateParams_ = $stateParams;

    /**
     * @private
     */
    this.graphhopperUrl_ = graphhopperUrl;

    /**
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * @type {app.MobileInteractionService}
     * @private
     */
    this.mobileInteractionService_ = appMobileInteractionService;

    /**
     * @type {ol.Map}
     * @private
     */
    this.map_ = appMapService.map;

    /**
     * @private
     */
    this.queryStateService_ = appQueryStateService;

    /**
     * @export
     */
    this.appWaiting = appWaiting;

    /**
     * @type {ol.Feature}
     * @export
     */
    this.modifyFeature;

    /**
     * @type {function(appx.TrackEditInitEvent)}
     * @export
     */
    this.onInitDrawing;

    /**
     * @type {function()}
     * @export
     */
    this.onCancelDrawing;

    /**
     * @type {function(appx.TrackEditChangeEvent)}
     * @export
     */
    this.onGeometryChanged;

    /**
     * @type {function(appx.TrackEditSaveEvent)}
     * @export
     */
    this.onSaveDrawing;

    /**
     * @export
     * @type {appx.UserSettings}
     */
    this.userSettings;

    /**
     * @type {app.interaction.DrawTrack|app.interaction.ModifyTrack}
     * @private
     */
    this.interaction_;

    /**
     * @type {Array.<ol.EventsKey>}
     * @private
     */
    this.listenerKeys_ = [];

    /**
     * @private
     */
    this.authentication_ = appAuthenticationService;


    this.snapToNetwork = snapToNetwork();

    /**
     * Requests info from swisstopo
     * @export
     * @type {function}
     */
    this.requestInfoForGeom;
  }

  $onInit() {
    this.authentication_.isUserLoggedIn((loggedIn, subscriptionOk) => {
      if (!loggedIn || !subscriptionOk) {
        return;
      }
      this.onInitDrawing({
        create: this.stateParams_['create']
      });
      this.scope_.$watch(
        () => this.modifyFeature,
        () => {
          // deactivate query feature
          this.queryStateService_.addQueryBlocker('tracks');
          if (this.modifyFeature) {
            // update mode
            this.interaction_ = new appInteractionModifyTrack(/** @type {app.interaction.Track.TrackOptions} */ ({
              color: this.modifyFeature.get('color_rgb'),
              graphhopperUrl: this.graphhopperUrl_,
              routingProfile: 'neutral',
              elevation: true,
              snap: true,
              requestInfoForGeom: this.requestInfoForGeom
            }));
            this.interaction_.setTrack(this.modifyFeature);
          } else {
            // create mode
            this.interaction_ = new appInteractionDrawTrack(/** @type {app.interaction.Track.TrackOptions} */ ({
              color: this.userSettings.color,
              graphhopperUrl: this.graphhopperUrl_,
              routingProfile: 'neutral',
              elevation: true,
              snap: true,
              requestInfoForGeom: this.requestInfoForGeom
            }));

            $(document).on('keydown.appTrackEdit', this, (event) => {
              if (event) {
                const controller = event.data;
                // delete key removes last point unless the user is editing the track name
                if (event.which === 46 && event.target.tagName !== 'INPUT') {
                  controller.deleteLastPoint();
                }
              }
            });

            this.listenerKeys_.push(
              listen(this.map_, 'dblclick', () => {
                this.interaction_.removeLastPoint();
                this.saveDrawing();
              })
            );
          }

          /**
           * Register events to track waiting application state during
           * which the application is not usable for the user.
           */
          this.listenerKeys_.push(
            listen(this.interaction_, 'waitingstarted', () => {
              this.appWaiting.increment();
              this.scope_.$apply();
            })
          );
          this.listenerKeys_.push(
            listen(this.interaction_, 'waitingfinished', () => {
              this.appWaiting.decrement();
              this.scope_.$apply();
            })
          );

          const layers = this.ngeoLocation_.getParam('layers');
          this.selectSnappingNetwork_(layers !== undefined ? layers.split(',') : []);
          this.scope_.$on('layersUpdated', (event, layers) => {
            this.selectSnappingNetwork_(layers);
          });

          this.listenerKeys_.push(
            listen(this.interaction_, 'segmentchanged', () => {
              this.refreshFacts_();
            })
          );

          this.map_.addInteraction(this.interaction_);
          this.enableDrawing_();

          this.scope_.$on(TRACK_EVENT_NAME.DELETE_LAST_POINT, () => {
            this.deleteLastPoint();
          });

          this.scope_.$on(TRACK_EVENT_NAME.CANCEL_DRAWING, () => {
            this.mainCtrl_.showMenu('mytracks', true);
            this.cancelDrawing();
          });

          this.scope_.$on(TRACK_EVENT_NAME.FINISH_DRAWING, () => {
            this.mainCtrl_.showMenu('mytracks', true);
          });

          this.scope_.$on(MAIN_MENU_EVENT_NAME.EXPAND, () => {
            if (smallScreen()) {
              this.finishMobileDrawing();
            }
          });

          if (smallScreen()) {
            this.startMobileDrawing();
          }
        });
    });

  }

  $onDestroy() {
    this.deactivate_();
  }

  /**
   * @private
   */
  enableDrawing_() {
    this.interaction_.setActive(true);
  }

  /**
   * @private
   */
  disableDrawing_() {
    this.interaction_.setActive(false);
  }

  /**
   * @private
   * @param {Array.<string>} activeLayers
   */
  selectSnappingNetwork_(activeLayers) {
    let profile = 'neutral';
    let previousProfile;

    for (let i = 0; i < activeLayers.length; i++) {
      const layer = activeLayers[i];
      if (layer.match('^Wander')) {
        profile = 'wander';
      } else if (layer.match('^Velo')) {
        profile = 'velo';
      } else if (layer.match('^Mountainbike') || layer.match('^Mtbland')) {
        profile = 'mtb';
      } else if (layer.match('^Skating')) {
        profile = 'skating';
      } else if (layer.match('^Kanuland') || layer.match('^Slowup')) {
        profile = 'neutral';
      }
      // if there are more than one kind of profile, use the 'all' profile
      if (previousProfile && previousProfile !== profile) {
        profile = 'all';
        break;
      }
      previousProfile = profile;
    }

    this.routingProfile_ = profile;
    this.interaction_.routingProfile = profile;
  }

  /**
   * @private
   */
  async refreshFacts_() {
    const line = this.interaction_.getLineString();
    if (this.modifyFeature) {
      this.modifyFeature.getGeometry().setCoordinates(line.getCoordinates());
    }
    const profile = await this.interaction_.getHeightProfile();
    this.onGeometryChanged({
      line: line,
      heightProfile: profile
    });
  }

  /**
   * @export
   */
  deleteLastPoint() {
    this.interaction_.removeLastPoint();
  }

  /**
   * @private
   */
  deactivate_() {
    if (this.interaction_) {
      this.interaction_.setActive(false);
      this.interaction_.clear();
      this.map_.removeInteraction(this.interaction_);
    }

    this.listenerKeys_.forEach(unlistenByKey);
    this.listenerKeys_.length = 0;

    $(document).off('keydown.appTrackEdit');

    this.queryStateService_.removeQueryBlocker('tracks');

    this.mobileInteractionService_.enableHideMenuOnMobile();
  }

  /**
   * @export
   */
  cancelDrawing() {
    this.deactivate_();
    this.onCancelDrawing();
  }

  /**
   * @return {boolean}
   * @export
   */
  get snapping() {
    return this.interaction_ && this.interaction_.snap;
  }

  /**
   * @param {boolean} value
   * @export
   */
  set snapping(value) {
    if (this.interaction_) {
      this.interaction_.snap = value;
    }
  }

  /**
   * @export
   */
  saveDrawing() {
    this.onSaveDrawing({
      line: this.interaction_.getLineString(),
      controlPoints: this.interaction_.controlPoints
    });
  }

  startMobileDrawing() {
    this.mobileInteractionService_.disableHideMenuOnMobile();
    this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.COLLAPSE);
  }

  /**
   * @export
   */
  finishMobileDrawing() {
    this.disableDrawing_();

    // re-enable the drawing on map click
    this.listenerKeys_.push(
      listenOnce(this.map_, 'singleclick', () => {
        this.enableDrawing_();
        this.startMobileDrawing();
      })
    );
  }

  reverseDirection() {
    this.interaction_.reverseTrack();
    this.refreshFacts_();
  }

  async snapTrack() {
    if (new URLSearchParams(document.location.search).get('old_snapping')) {
      return this.snapTrackLegacy();
    }
    this.appWaiting.increment();
    const geom = this.modifyFeature.getGeometry();
    const origViapoints = this.modifyFeature.get('via_points');
    const viapointRatios = measurePointsAlongLineString(origViapoints, geom, 1);
    const newGeom = await matchBestEffort(geom, this.graphhopperUrl_, this.routingProfile_);
    const newCoordinates = newGeom.getCoordinates();
    geom.setCoordinates(newCoordinates);
    const newViapointIndices = snapCoordinatesToLineString(viapointRatios, geom, 1);
    // control points are historically 2D (see routeSegmentIfPossible_)
    const newViapointCoordinates = newViapointIndices.map(i => newCoordinates[i].slice(0, 2));
    this.modifyFeature.set('via_points', newViapointCoordinates);
    this.interaction_.setTrack(this.modifyFeature);
    this.interaction_.controlPoints.map(p => p.set('snapped', true));
    this.refreshFacts_();
    this.appWaiting.decrement();
    this.scope_.$apply();
  }

  snapTrackLegacy() {
    this.appWaiting.increment();
    const trackLength = this.modifyFeature.get('meta').length;
    // calculate max count of vertexes depends on track length
    const coef = trackLength > 10000 ? 100 : 10;
    const maxLength = Math.round(this.modifyFeature.get('meta').length / coef);
    let coords = this.modifyFeature.getGeometry().getCoordinates().map(coord => [coord[0], coord[1]]);
    // if count of coordinates more than max value gets elements from equally
    if (coords.length > maxLength) {
      // remove last element from array to not lost it
      const lastElement = coords.splice(coords.length - 1, 1);
      const reducePointsCount = () => {
        let n = Math.round(coords.length / (coords.length - maxLength));
        n = n < 2 ? 2 : n;
        coords = coords.filter((value, i) => (i + 1) % n);
        if (coords.length > maxLength) {
          reducePointsCount();
        } else {
          // add the last element back
          coords.push(lastElement[0]);
        }
      };
      reducePointsCount();
    }
    this.modifyFeature.set('via_points', coords);
    this.interaction_.setTrack(this.modifyFeature, true);
    this.interaction_.snapTrack().then(() => {
      // calculate new via points
      const length = this.modifyFeature.get('meta').length;
      const trackpoints = this.modifyFeature.getGeometry().getCoordinates();
      const vpoints = calculateViaPoints(trackpoints, length);
      this.modifyFeature.set('via_points', vpoints);
      this.interaction_.setTrack(this.modifyFeature);
    }).finally(() => {
      this.appWaiting.decrement();
    });
  }

  onLineStyleChanged(color_rgb, opacity) {
    this.interaction_.updateSketchLineColor(color_rgb, opacity / 100);
  }

  onBikeSpeedChanged() {
    this.refreshFacts_();
  }

}


/**
 * @const {!angular.Component}
 */
const component = {
  bindings: {
    'modifyFeature': '<',
    'onGeometryChanged': '&',
    'onInitDrawing': '&',
    'onCancelDrawing': '&',
    'onSaveDrawing': '&',
    'userSettings': '<',
    'factsLoading': '=?',
    'requestInfoForGeom': '<',
  },
  controller: Controller,
  templateUrl: 'js/app/track/edit/partial-bottom.html'
};

export default appBase.component('appTrackEdit', component);

